import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Zap, Globe, Users, Shield, Award, TrendingUp, Heart, Clock, Sparkles, RotateCcw, Star } from 'lucide-react';
import { ExternalLink } from 'lucide-react';
import { AnimatedCounter } from './AnimatedCounter';

const features = [
  'Verified Domains Only',
  'Secure Connection',
  'Official Support'
];

export const AccessInfoSection = () => {
  return (
    <section className="py-4 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-4xl mx-auto glass rounded-2xl p-6 md:p-8 text-center space-y-3 relative"
        style={{ boxShadow: 'var(--shadow-glass)' }}
      >
        <div className="mb-8">
          <div className="flex flex-col items-center justify-center gap-2 mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 shadow-[0_0_15px_rgba(34,197,94,0.1)]">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-wider">Trusted Partner Service</span>
            </div>
            <p className="text-[10px] text-muted-foreground/80 uppercase tracking-widest">
              Fulfilled by XtreamCube
            </p>
          </div>

          <h3 className="text-sm font-semibold text-primary mb-3 uppercase tracking-wide">Quick Access</h3>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch">
            <motion.a
              href="https://xtreamcube.shop/freetrialiptv/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, backgroundColor: 'rgba(34, 197, 94, 0.15)' }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary/10 border border-primary/30 hover:border-primary/50 transition text-primary font-semibold group"
            >
              <Sparkles className="w-5 h-5 group-hover:animate-spin shrink-0" />
              <span>Free Trial - 24 Hours</span>
              <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 shrink-0" />
            </motion.a>

            <motion.a
              href="https://xtreamcube.shop/renew/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, backgroundColor: 'rgba(34, 197, 94, 0.15)' }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary/10 border border-primary/30 hover:border-primary/50 transition text-primary font-semibold group"
            >
              <RotateCcw className="w-5 h-5 group-hover:animate-spin shrink-0" />
              <span>Renew Account</span>
              <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 shrink-0" />
            </motion.a>
          </div>
          <p className="text-xs text-muted-foreground mt-3 max-w-lg mx-auto">
            Service delivery and support are managed directly by <strong>XtreamCube</strong>.
          </p>
        </div>

        <div className="border-b border-white/10 pb-6"></div>

        <h2 className="text-2xl sm:text-3xl font-bold mt-6">
          Official XtreamCube Access
        </h2>

        <p className="text-muted-foreground leading-relaxed">
          This gateway directs you to the official, verified XtreamCube properties. All services
          and content delivery are managed exclusively through these trusted domains.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-6 pt-2">
          {features.map((feature, index) => (
            <motion.div
              key={feature}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
              className="flex items-center gap-2 text-primary"
            >
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">{feature}</span>
            </motion.div>
          ))}
        </div>

        <div className="pt-6 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="space-y-1"
            >
              <div className="text-2xl font-bold text-primary">24/7</div>
              <div className="text-sm text-muted-foreground">Round-the-clock Support</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.4 }}
              className="space-y-1"
            >
              <div className="text-2xl font-bold text-primary">Expert</div>
              <div className="text-sm text-muted-foreground">Professional Team</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="space-y-1"
            >
              <div className="text-2xl font-bold text-primary">24h Free</div>
              <div className="text-sm text-muted-foreground">Trial Period</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.6 }}
              className="space-y-1"
            >
              <div className="text-2xl font-bold text-primary">Easy Renewals</div>
              <div className="text-sm text-muted-foreground">Hassle-free experience</div>
            </motion.div>
          </div>
        </div>

        <div className="pt-6 border-t border-white/10">
          <h3 className="text-lg font-semibold mb-4">Premium Features Included</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="flex gap-3 items-start p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
            >
              <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <div className="font-semibold text-sm">20,000+ Channels & VOD</div>
                <div className="text-xs text-muted-foreground">Full HD & 4K streaming</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.4 }}
              className="flex gap-3 items-start p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
            >
              <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <div className="font-semibold text-sm">25 Global Servers</div>
                <div className="text-xs text-muted-foreground">Worldwide coverage & low latency</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="flex gap-3 items-start p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
            >
              <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <div className="font-semibold text-sm">Multi-Device Support</div>
                <div className="text-xs text-muted-foreground">Smart TV, Mobile, Fire Stick & more</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.6 }}
              className="flex gap-3 items-start p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
            >
              <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <div className="font-semibold text-sm">VPN Enabled & Secure</div>
                <div className="text-xs text-muted-foreground">Protected connections worldwide</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.7 }}
              className="flex gap-3 items-start p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
            >
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <div className="font-semibold text-sm">Instant Delivery</div>
                <div className="text-xs text-muted-foreground">Setup in minutes, no delays</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.8 }}
              className="flex gap-3 items-start p-3 rounded-lg bg-white/5 hover:bg-white/10 transition"
            >
              <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <div className="font-semibold text-sm">Anti-Freeze Technology</div>
                <div className="text-xs text-muted-foreground">Smooth streaming guaranteed</div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          <h3 className="text-lg font-semibold mb-5">Trusted by Thousands Worldwide</h3>

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 border border-primary/40"
          >
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-xs font-semibold text-primary">4.9/5 Rating (10K+ Reviews)</span>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30"
            >
              <Award className="w-6 h-6 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">Since 2017</div>
              <div className="text-xs text-muted-foreground mt-1">Industry Pioneer</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30"
            >
              <Users className="w-6 h-6 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">
                <AnimatedCounter from={0} to={27500} duration={2} suffix="+" />
              </div>
              <div className="text-xs text-muted-foreground mt-1">Active Clients</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30"
            >
              <TrendingUp className="w-6 h-6 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">150+</div>
              <div className="text-xs text-muted-foreground mt-1">Countries Served</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.6 }}
              className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30"
            >
              <Heart className="w-6 h-6 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">95%+</div>
              <div className="text-xs text-muted-foreground mt-1">Retention Rate</div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="mt-5 p-4 rounded-lg bg-white/5 border border-white/10"
          >
            <div className="flex items-center gap-2 justify-center mb-2">
              <Clock className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Customers Staying with Us for Years</span>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Long-term loyalty proves our commitment to quality, reliability, and exceptional service
            </p>
          </motion.div>
        </div>

        <p className="text-sm text-muted-foreground pt-6">
          <span className="font-semibold text-foreground">Need assistance?</span> Visit any official
          domain below to access support options, or inquiries. Our team is available
          to help through official channels.
        </p>
      </motion.div>
    </section>
  );
};